import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';
import type { GridColumnDef } from './grid-columns';
import { type FilterRule, type FilterOperator, getOperatorsForColumn, operatorNeedsValue } from './grid-columns';

interface FilterBarProps {
  filters: FilterRule[];
  onFiltersChange: (filters: FilterRule[]) => void;
  columns: GridColumnDef[];
}

let nextFilterId = 1;

// ── Searchable column dropdown ─────────────────────────────────────────────

function ColumnDropdown({
  value,
  columns,
  onChange,
}: {
  value: string;
  columns: GridColumnDef[];
  onChange: (field: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return columns;
    const q = search.toLowerCase();
    return columns.filter(c => c.title.toLowerCase().includes(q));
  }, [columns, search]);

  const selectedTitle = columns.find(c => c.field === value)?.title ?? value;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 hover:border-blueberry-400 transition-colors min-w-[120px]"
      >
        <span className="truncate flex-1 text-left">{selectedTitle}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0 text-nightshade-400" />
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-56 bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-xl shadow-lg overflow-hidden">
          <div className="p-1.5">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search columns..."
              className="w-full text-xs bg-nightshade-50 dark:bg-nightshade-700 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-xs text-nightshade-400 px-3 py-2">No columns match</p>
            )}
            {filtered.map(c => (
              <button
                key={c.id}
                onClick={() => { onChange(c.field); setOpen(false); }}
                className={`w-full text-left text-xs px-3 py-1.5 transition-colors ${
                  c.field === value
                    ? 'bg-blueberry-50 dark:bg-blueberry-900/30 text-blueberry-600 dark:text-blueberry-400 font-medium'
                    : 'text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-700'
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FilterBar ──────────────────────────────────────────────────────────────

export function FilterBar({ filters, onFiltersChange, columns }: FilterBarProps) {
  const columnMap = useMemo(() => new Map(columns.map(c => [c.field, c])), [columns]);

  const addFilter = useCallback(() => {
    const first = columns[0];
    if (!first) return;
    const ops = getOperatorsForColumn(first);
    onFiltersChange([
      ...filters,
      { id: String(nextFilterId++), field: first.field, operator: ops[0].value, value: '' },
    ]);
  }, [filters, onFiltersChange, columns]);

  const updateFilter = useCallback((id: string, patch: Partial<FilterRule>) => {
    onFiltersChange(filters.map(f => {
      if (f.id !== id) return f;
      const updated = { ...f, ...patch };
      // If field changed, reset operator to first valid one for new column
      if (patch.field && patch.field !== f.field) {
        const col = columnMap.get(patch.field);
        if (col) {
          const ops = getOperatorsForColumn(col);
          updated.operator = ops[0].value;
          updated.value = '';
        }
      }
      // If operator changed to one that doesn't need a value, clear it
      if (patch.operator && !operatorNeedsValue(patch.operator)) {
        updated.value = '';
      }
      return updated;
    }));
  }, [filters, onFiltersChange, columnMap]);

  const removeFilter = useCallback((id: string) => {
    onFiltersChange(filters.filter(f => f.id !== id));
  }, [filters, onFiltersChange]);

  const clearAll = useCallback(() => {
    onFiltersChange([]);
  }, [onFiltersChange]);

  return (
    <div className="mb-3 space-y-2">
      {filters.map(f => {
        const col = columnMap.get(f.field);
        const operators = col ? getOperatorsForColumn(col) : [];
        const needsValue = operatorNeedsValue(f.operator);

        return (
          <div key={f.id} className="flex items-center gap-2 flex-wrap">
            {/* Searchable column dropdown */}
            <ColumnDropdown
              value={f.field}
              columns={columns}
              onChange={field => updateFilter(f.id, { field })}
            />

            {/* Operator dropdown */}
            <select
              value={f.operator}
              onChange={e => updateFilter(f.id, { operator: e.target.value as FilterOperator })}
              className="text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            >
              {operators.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            {/* Value input */}
            {needsValue && (
              <input
                type="text"
                value={f.value}
                onChange={e => updateFilter(f.id, { value: e.target.value })}
                placeholder="Value..."
                className="text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 w-36"
              />
            )}

            {/* Remove button */}
            <button
              onClick={() => removeFilter(f.id)}
              className="p-1 text-nightshade-400 hover:text-pomegranate-500 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}

      <div className="flex items-center gap-3">
        <button
          onClick={addFilter}
          className="flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-600 border border-dashed border-blueberry-300 dark:border-blueberry-700 rounded-full h-8 px-3.5 hover:bg-blueberry-50 dark:hover:bg-blueberry-900/20 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add filter
        </button>
        {filters.length > 0 && (
          <button
            onClick={clearAll}
            className="text-xs text-nightshade-400 hover:text-pomegranate-500 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
